import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { DeliveryForecastDespatchAdvicePackagingMaterial } from '../../../../../../../data/models'
import { useMode } from '../../../../../../../hooks'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../../utils/helper/validateFormikFields'
import { useConfigContext } from '../../../../../../ui-config'
import { FormFieldName, FormValues } from '../common'
import { EditPackagingDataView } from './EditPackagingData.view'

type Props = {
  handleSubmit: (formData: any) => Promise<void>
  isRequestRunning: boolean
  packaging?: DeliveryForecastDespatchAdvicePackagingMaterial
  handleClose: () => void
}
export const EditPackagingDataForm: React.FC<Props> = ({
  handleSubmit,
  isRequestRunning,
  packaging,
  handleClose,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()
  const mode = useMode()

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      packagingDataId: packaging?.PackagingDataId,
      description: packaging?.Description,
      packagingMaterial: packaging?.PackagingMaterial,
      length: packaging?.Measurements?.Length,
      width: packaging?.Measurements?.Width,
      height: packaging?.Measurements?.Height,
      measurementsUnit: packaging?.Measurements?.Unit,
      tareWeight: packaging?.TareWeight?.Value,
      tareWeightUnit: packaging?.TareWeight?.Unit,
      volume: packaging?.Volume?.Value,
      volumeUnit: packaging?.Volume?.Unit,
      packagingIdentifier: packaging?.PackagingIdentifier,
      propertyIdentifier: packaging?.PropertyIdentifier,
      isStackable: packaging?.IsStackable,
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'packagingDataId',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .packagingDataId,
        },
        {
          fieldName: 'description',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .description,
        },
        {
          fieldName: 'packagingMaterial',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .packagingMaterial,
        },
        {
          fieldName: 'length',
          config:
            config.selfservices.masterDataManagement.packagingData.edit.length,
        },
        {
          fieldName: 'width',
          config:
            config.selfservices.masterDataManagement.packagingData.edit.width,
        },
        {
          fieldName: 'height',
          config:
            config.selfservices.masterDataManagement.packagingData.edit.height,
        },
        {
          fieldName: 'measurementsUnit',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .measurementsUnit,
        },
        {
          fieldName: 'tareWeight',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .tareWeight,
        },
        {
          fieldName: 'tareWeightUnit',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .tareWeightUnit,
        },
        {
          fieldName: 'volume',
          config:
            config.selfservices.masterDataManagement.packagingData.edit.volume,
        },
        {
          fieldName: 'volumeUnit',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .volumeUnit,
        },
        {
          fieldName: 'packagingIdentifier',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .packagingIdentifier,
        },
        {
          fieldName: 'propertyIdentifier',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .propertyIdentifier,
        },
        {
          fieldName: 'isStackable',
          config:
            config.selfservices.masterDataManagement.packagingData.edit
              .isStackable,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      if (!formData.packagingDataId) {
        errors.packagingDataId = t(
          'masterDataManagement.packagingData.table.edit.dialog.form.packagingDataId.validation.required',
        )
      }

      if (!formData.description) {
        errors.description = t(
          'masterDataManagement.packagingData.table.edit.dialog.form.description.validation.required',
        )
      }

      if (
        formData.packagingIdentifier === 'reusablePackaging' &&
        !formData.propertyIdentifier
      ) {
        errors.propertyIdentifier = t('common.errors.required')
      } else {
        delete errors.propertyIdentifier
      }

      return errors
    },
    onSubmit: async formData => {
      await handleSubmit(formData)

      formik.resetForm()
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // Helper function to get the correct config based on mode
  const getModeConfig = () => {
    const masterDataConfig =
      config.selfservices.masterDataManagement.packagingData
    switch (mode) {
      case 'add':
        return masterDataConfig.add
      case 'info':
        return masterDataConfig.info
      case 'edit':
      default:
        return masterDataConfig.edit
    }
  }

  const modeConfig = getModeConfig()

  return (
    <EditPackagingDataView
      formik={formik}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
      config={modeConfig}
      isRequestRunning={isRequestRunning}
      handleClose={handleClose}
    />
  )
}
